// The client record's working parts (UI rebuild batch 5) — COPIED from today's screens/OnboardingRecord.tsx, the logic
// unchanged, the drawing lighter (RULING-LIGHT): asking the customer (POST /api/onboarding/requests, ?ask=), what was
// asked and the answers, their phone number with WhatsApp a tap away, Make a party (POST /api/onboarding/party, with the
// name-taken choice), the conversation and the history. Nothing here decides: Approve / Reject stay on the card in the
// Telegram ops group.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { fmtAmount, fmtDate, fmtDay, fmtTime } from '../../../lib/format';
import { sameAsset } from '../../../lib/naming';
import { Address, EmptyState } from '../kit';
import { Clip, OutButton, Parts, WaitingLine, WalletLineView, failWords, openChat } from '../../OnboardingParts';
import {
  ASKABLE, CHECKS, ITEMS, REQUEST_STATE_WORD, askedWords, fmtPhone, heldOf, isLive, liveLine, liveReading, madePartyWords, onboardingApi,
  partyNameTaken, phoneHow, requestLabel, saleCoin, shortfall, walletLine,
  type ArkhamAnswer, type Asked, type CheckKey, type LeadRecord, type PartyLink, type RequestItem, type RequestView,
} from '../../../lib/onboarding';

/** A label and its value: every label in one column from sm, stacked on a phone. */
export function Facts({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-4 gap-y-2 max-sm:grid-cols-1 max-sm:gap-y-1">{children}</dl>;
}
export function Fact({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt className="pt-0.5 text-meta text-tg-hint">{k}</dt>
      <dd className="min-w-0 break-words text-body leading-snug max-sm:mb-1" data-line={k}>{children}</dd>
    </>
  );
}
const yesNo = (v: boolean | null): ReactNode => (v === null ? <span className="text-tg-hint">Not answered yet</span> : v ? 'Yes' : <span className="font-medium text-neg">No</span>);

// ── their phone number ───────────────────────────────────────────────────────────────────────────────────────────

/** "+971 50 999 8877 · verified by Telegram", WhatsApp a tap away — or that they would rather not give one. */
export function PhoneLine({ p }: { p: NonNullable<LeadRecord['phone']> }) {
  const how = p.number ? phoneHow(p) : null;
  const wa = p.number ? `https://wa.me/${p.number.slice(1)}` : null;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body" data-phone={p.number ? (p.verified ? 'verified' : p.via ?? 'unknown') : p.declined ? 'declined' : 'unreadable'}>
      {p.number ? (
        <>
          <span className="tabular-nums">
            <span className="whitespace-nowrap">{fmtPhone(p.number)}</span>
            {how && <>{' · '}<span className={p.verified ? 'text-pos' : 'text-tg-hint'}>{how}</span></>}
          </span>
          {wa && (
            <a className="btn-link text-body" href={wa} target="_blank" rel="noopener noreferrer" aria-label={`Open WhatsApp with ${fmtPhone(p.number)}`}
              onClick={(e) => { e.preventDefault(); openChat(wa); }}>
              WhatsApp
            </a>
          )}
        </>
      ) : p.declined
        ? <span className="text-tg-hint">No phone number given</span>
        : <span className="text-tg-hint">Phone on record, not shown</span>}
    </p>
  );
}

// ── what is still missing ────────────────────────────────────────────────────────────────────────────────────────

/** What is still missing, in the order the bot asks, and — when the chat waits on them — whether the bot can still
 *  write to them (amber, the team's to do, once it cannot). One line, as the list's. */
export function StillMissing({ d, readMs }: { d: LeadRecord; readMs: number }) {
  const missing = d.stillMissing ?? [];
  if (missing.length === 0 && !d.waitingOn) return null;
  const waited = d.waitingOn?.items ?? [];
  const said = missing.length > 0 && waited.length > 0 && waited.every((x) => missing.some((m) => m.key === x.key));
  return (
    <div className="space-y-1" data-still-missing="">
      {missing.length > 0 && (
        <p className="text-body"><span className="text-tg-hint">Still missing: </span><span className="text-warn">{missing.map((m) => m.label).join(' · ')}</span></p>
      )}
      {d.waitingOn && <WaitingLine w={d.waitingOn} channel={d.channel} readMs={readMs} items={!said} />}
    </div>
  );
}

// ── asking the customer ──────────────────────────────────────────────────────────────────────────────────────────

/** "Ask the customer" for one check: its own items and "Something else", a note of at most 200 characters. An item
 *  already waiting is not asked twice. Send asks the BFF — signed by whoever is signed in — and says when the bot asks;
 *  the record and the lists are read again. */
export function AskForm({ d, check, close }: { d: LeadRecord; check: CheckKey; close: () => void }) {
  const qc = useQueryClient();
  const name = CHECKS.find((k) => k.key === check)?.name ?? check;
  const waiting = new Set((d.requests ?? []).filter(isLive).map((r) => r.item).filter((x): x is RequestItem => x !== null));
  const offered: RequestItem[] = [...ASKABLE[check], 'other'];
  const [picked, setPicked] = useState<RequestItem[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; tone: 'plain' | 'warn' } | null>(null);
  const other = picked.includes('other');
  const trimmed = note.trim();
  const problem = picked.length === 0 ? 'Choose what to ask for.' : other && !trimmed ? '"Something else" needs a note.' : null;
  const send = async () => {
    if (problem || busy) return;
    setBusy(true);
    setSaid(null);
    try {
      const r: Asked = await onboardingApi.ask(d.id, picked, trimmed || null);
      setSaid(askedWords(r));
      setPicked([]);
      setNote('');
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    } catch (e) {
      setSaid({ text: `Couldn't ask — ${failWords(e)}`, tone: 'warn' }); // words:ok — the server's sentence
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-lg border border-stroke bg-panel-2 px-4 py-3" role="group" aria-label={`Ask the customer — ${name}`} data-ask-form={check}>
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-body font-medium">Ask the customer — {name}</h4>
        <button type="button" onClick={close} aria-label="Close the form" className="-mr-2 -mt-2 grid h-11 w-11 place-items-center rounded-md text-tg-hint hover:bg-panel lg:h-10 lg:w-10"><X className="h-4 w-4" aria-hidden /></button>
      </div>
      <ul className="grid gap-1 sm:grid-cols-2" aria-label="What to ask for">
        {offered.map((item) => {
          const already = waiting.has(item);
          return (
            <li key={item}>
              <label className={`flex min-h-[44px] items-center gap-2 text-body lg:min-h-[40px] ${already ? 'text-tg-hint' : ''}`}>
                <input type="checkbox" className="h-4 w-4 accent-brand-gold" disabled={already || busy} checked={picked.includes(item)}
                  onChange={(e) => setPicked((p) => (e.target.checked ? [...p, item] : p.filter((x) => x !== item)))} data-item={item} />
                <span>{ITEMS[item].label}{already && <span className="text-tg-hint"> — already asked</span>}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <label className="block space-y-1">
        <span className="block text-meta text-tg-hint">{other ? 'What to ask them' : 'Note for the bot (optional)'}</span>
        <textarea className="input min-h-[64px] w-full resize-y" maxLength={200} rows={2} value={note} disabled={busy}
          onChange={(e) => setNote(e.target.value)} placeholder={other ? 'e.g. the name of their company' : 'Anything the bot should know'} />
        <span className="block text-right text-meta tabular-nums text-tg-hint">{note.length}/200</span>
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-secondary px-4 text-body disabled:opacity-50" disabled={!!problem || busy} onClick={() => void send()}>
          {busy ? 'Asking…' : 'Send'}
        </button>
        <button type="button" className="btn-link text-body" onClick={close}>Cancel</button>
        {said
          ? <span className={`text-body ${said.tone === 'warn' ? 'text-warn' : 'text-pos'}`} data-asked={said.tone}>{said.text}</span>
          : problem && picked.length > 0 && <span className="text-body text-warn">{problem}</span>}
      </div>
    </div>
  );
}

/** What was asked for one section, newest first: what, where it stands (answered green; declined or no answer amber),
 *  who asked and when, the team's note, their answer. */
export function RequestTrail({ list }: { list: RequestView[] }) {
  if (list.length === 0) return null;
  const tone = (s: RequestView['state']) => (s === 'answered' ? 'text-pos' : s === 'declined' || s === 'no_answer' ? 'text-warn' : 'text-tg-hint');
  return (
    <ul className="space-y-2" aria-label="Asked of the customer">
      {list.map((r, i) => {
        const when = [
          r.by || r.at ? `requested${r.by ? ` by ${r.by}` : ''}${r.at ? ` · ${fmtDate(r.at)}` : ''}` : null,
          r.askedAt ? `sent ${fmtDate(r.askedAt)}` : null,
          r.closedAt && r.state !== 'open' && r.state !== 'asked' ? `${r.state === 'answered' ? 'answered' : 'closed'} ${fmtDate(r.closedAt)}` : null,
        ].filter(Boolean).join(' · ');
        return (
          <li key={r.id ?? `r:${i}`} className="text-body leading-snug" data-request-state={r.state ?? 'unknown'}>
            <span className="font-medium">Asked: {requestLabel(r)}</span>
            {' · '}<span className={tone(r.state)}>{r.state ? REQUEST_STATE_WORD[r.state] : 'State not known'}</span>
            {when && <span className="block text-meta tabular-nums text-tg-hint"><Parts text={when} /></span>}
            {r.note && <span className="block text-meta text-tg-hint">Note: “{r.note}”</span>}
            {r.answer && <span className="block text-body">Answer: “{r.answer}”</span>}
          </li>
        );
      })}
    </ul>
  );
}

/** A section's requests: those of its checks (null: none — a contact, the team's own question). */
export const trailOf = (d: LeadRecord, checks: Array<CheckKey | null>): RequestView[] => (d.requests ?? []).filter((r) => checks.includes(r.check));

// ── the wallet's evidence ────────────────────────────────────────────────────────────────────────────────────────

const ARKHAM_NOT_RUN: Record<string, string> = {
  unscreened: 'Not screened', error: 'The check failed — open Arkham', not_enabled: 'Arkham is not switched on', budget_exhausted: 'Not checked — budget ran out',
};
/** Arkham's stored answer, as information: who it says the wallet is, its band and labels. Never "clean" from a check
 *  that did not run. */
function Arkham({ a }: { a: ArkhamAnswer | null }) {
  if (!a) return <span className="text-tg-hint">Not checked yet</span>;
  if (a.status !== 'clean' && a.status !== 'flagged') return <span className="text-warn">{a.status ? ARKHAM_NOT_RUN[a.status] ?? a.status : 'Answer not readable'}</span>;
  return (
    <span className="block">
      <span>{a.status === 'flagged' && <span className="font-medium text-neg">Flagged · </span>}{a.entity ?? 'No entity named'}{a.band && <> · <span className="tabular-nums">{a.band}{a.score !== null && ` (${a.score})`}</span></>}</span>
      {a.labels.length > 0 && <span className="block text-meta text-tg-hint">{a.labels.join(' · ')}</span>}
      {a.checkedAt && <span className="block text-meta text-tg-hint">checked {fmtDate(a.checkedAt)}</span>}
    </span>
  );
}
const RISK_WORD: Record<string, string> = { clean: 'Clean · no score', flagged: 'Flagged · no score', unscreened: 'Not scored yet', error: 'The check failed', none: 'Not checked', pending: 'Check pending' };
function Risk({ w }: { w: NonNullable<LeadRecord['wallet']> }) {
  if (w.band) {
    const tone = w.band === 'severe' ? 'font-medium text-neg' : w.band === 'high' ? 'font-medium text-warn' : '';
    // The score is Global Ledger's (the record's contract, lib/api.ts walletCheck.score): named, not "GL".
    return <span className={`whitespace-nowrap ${tone}`}>{`${w.band}${w.score !== null ? ` (${w.score})` : ''}`}<span className="ml-2 text-meta font-normal text-tg-hint">Global Ledger</span></span>;
  }
  return <span className={w.status === 'flagged' || w.status === 'error' ? 'text-warn' : 'text-tg-hint'}>{w.status ? RISK_WORD[w.status] ?? w.status : 'Could not be read'}</span>;
}
const ORIGIN_WORD: Record<string, string> = { self_custody: 'their own wallet (self-custody)', exchange_withdrawal: 'from an exchange' };
const words = (map: Record<string, string>, code: string | null): string | null =>
  (code ? (Object.hasOwn(map, code) ? map[code] ?? code : code.replace(/_/g, ' ')) : null);

/** The wallet: what it holds now (read from its chain, with Read again) and at its check, against the claim, then who it
 *  is — sanctions, risk, Arkham, the address — with its transactions and Arkham one tap away. */
export function WalletEvidence({ d, readMs, reading, readAgain }: { d: LeadRecord; readMs: number; reading: boolean; readAgain: () => void }) {
  const a = d.answers;
  const w = d.wallet;
  const sale = saleCoin(a);
  const claimUsdt = w?.claimUsdt ?? null;
  const short = sale && w ? shortfall(heldOf(w, sale), claimUsdt?.low ?? null) : null;
  const links = w?.links ?? { transactions: null, arkham: null };
  const live = liveReading(d.walletLive) ? d.walletLive : null;
  const neverRead = !!d.walletLive && d.walletLive.ok === false && d.walletLive.readAt === null;
  if (!w && !live) return <p className="text-body text-tg-hint">No wallet given yet.</p>;
  return (
    <div className="space-y-3">
      {(links.transactions || links.arkham) && (
        <div className="flex flex-wrap gap-2"><OutButton href={links.transactions} label="Transactions" /><OutButton href={links.arkham} label="Arkham" /></div>
      )}
      <Facts>
        {(live || d.walletLive) && (
          <Fact k="Holds now">
            {live ? <WalletLineView line={liveLine(live, w, sale, false, readMs, w?.chain ?? null)} label={false} /> : <span className="text-tg-hint">No reading yet</span>}
            {neverRead && <span className="block text-body text-warn" data-live="never">The chain didn&apos;t answer</span>}
            {d.walletLive && (
              <button type="button" className="btn-link text-body disabled:opacity-60" disabled={reading} onClick={readAgain} data-read-again=""
                aria-label="Read the record again — the wallet's chain is read again when its reading is over a minute old">
                {reading ? 'Reading…' : 'Read again'}
              </button>
            )}
          </Fact>
        )}
        {w && (
          <Fact k={live ? 'At the check' : 'Holds'}>
            {w.holdings && (w.holdings.length > 0 || sale) ? (
              <span className="block tabular-nums">
                {sale && !w.holdings.some((h) => sameAsset(h.asset, sale)) && <span className="block font-medium text-neg">0 {sale}</span>}
                {w.holdings.map((h) => <span key={h.asset} className="block">{fmtAmount(h.amount, h.asset)} {h.asset}</span>)}
              </span>
            ) : <WalletLineView line={walletLine(w, sale, false, w.chain)} label={false} />}
          </Fact>
        )}
        {w && sale && typeof w.coversClaim === 'boolean' && (
          <Fact k="Against the claim">
            {w.coversClaim
              ? <span className="font-medium text-pos">Covers the claim</span>
              : short
                ? <span className="font-medium text-neg">{short.pct}% of the claim · short by {fmtAmount(Math.round(short.short), undefined, 0)} {sale}</span>
                : <span className="font-medium text-neg">Short of the claim</span>}
          </Fact>
        )}
        {w && (
          <>
            <Fact k="Sanctions">
              {w.sanctions === 'listed' ? <span className="font-medium text-neg">Listed — on a sanctions list</span>
                : w.sanctions === 'clear' ? 'Not listed' : w.sanctions === 'not_checked' ? <span className="text-warn">Not checked</span> : <span className="text-tg-hint">Not known</span>}
              {/* WHO SAID SO (ruled 1 Nov 2026: a verdict names its source): the list that named the address, else the lists the
                  check read, and the Dubai day those lists were last refreshed (KYT, main ada2efbb). A record from before
                  carries null — then nothing is said, never a guess. */}
              {w.sanctions && w.sanctions !== 'not_checked' && sanctionsSource(w) && (
                <span className="block text-meta text-tg-hint">
                  {sanctionsSource(w)}{sanctionsListAt(w) ? ` · lists of ${fmtDay(sanctionsListAt(w)!)}` : ''}
                </span>
              )}
            </Fact>
            <Fact k="Risk score"><Risk w={w} /></Fact>
            <Fact k="Arkham"><Arkham a={w.arkham ?? null} /></Fact>
            {w.address && <Fact k="Address"><Address value={w.address} /></Fact>}
            {(w.chain || w.origin) && <Fact k="Network">{[w.chain, words(ORIGIN_WORD, w.origin)].filter(Boolean).join(' · ')}</Fact>}
            {w.activity && <Fact k="Activity">{w.activity}</Fact>}
            {w.checkedAt && <Fact k="Checked"><span className="tabular-nums">{fmtDate(w.checkedAt)}</span></Fact>}
          </>
        )}
      </Facts>
    </div>
  );
}

/** Their answers, as the record holds them — drawn only where no checklist carries them (an older worker). */
export function Answers({ d }: { d: LeadRecord }) {
  const a = d.answers;
  return (
    <Facts>
      <Fact k="In their words">{a.size ?? <span className="text-tg-hint">Not said yet</span>}</Fact>
      {a.payout && <Fact k="Paid how">{a.payout}</Fact>}
      {a.fundsNote && <Fact k="Where the rest is">“{a.fundsNote}”</Fact>}
      <Fact k="Source of funds">{a.sourceOfFunds ? <Clip text={a.sourceOfFunds} /> : <span className="text-warn">Not answered yet</span>}</Fact>
      <Fact k="Purpose">{a.purpose ? <Clip text={a.purpose} /> : <span className="text-warn">Not answered yet</span>}</Fact>
      <Fact k="Own money">{yesNo(a.ownMoney)}</Fact>
      <Fact k="UAE resident">{yesNo(a.resident)}</Fact>
      <Fact k="In the UAE now">{yesNo(a.inUae)}</Fact>
      <Fact k="Nationality">{a.nationality ?? <span className="text-tg-hint">Not answered yet</span>}</Fact>
      <Fact k="Lives in">{a.jurisdiction ?? <span className="text-tg-hint">Not answered yet</span>}</Fact>
      {a.frequency && <Fact k="How often">{a.frequency === 'one_off' ? 'One-off' : a.frequency === 'recurring' ? 'Recurring' : a.frequency}</Fact>}
      {a.referredBy && <Fact k="Referred by">{a.referredBy}</Fact>}
      {d.caveats.length > 0 && <Fact k="Caveats"><ul className="space-y-1">{d.caveats.map((x, i) => <li key={i}>{x}</li>)}</ul></Fact>}
      {d.changed.length > 0 && (
        <Fact k="Changed since">
          <ul className="space-y-1">{d.changed.map((x) => <li key={x.field}><span className="text-tg-hint">{x.field}:</span> {x.card ?? 'not given'} → <span className="text-warn">{x.now ?? 'not given'}</span></li>)}</ul>
        </Fact>
      )}
      {d.priorContact.length > 0 && <Fact k="Seen before">{d.priorContact.join(' · ')}</Fact>}
      {d.checks.length > 0 && (
        <Fact k="ID against claim">
          <ul className="space-y-1">
            {d.checks.map((x, i) => <li key={i} className={x.ok ? '' : 'text-neg'}>{x.ok ? 'Matches' : 'Differs'}: {x.label}{x.note && <span className="text-tg-hint"> — {x.note}</span>}</li>)}
          </ul>
        </Fact>
      )}
    </Facts>
  );
}

/** Each ID image's message in the ops group, opened in Telegram (the operator, 14 Oct 2026: "how to find his ID
 *  document?") — and how many of the two were received. */
export function IdImages({ d }: { d: LeadRecord }) {
  const ids = d.idImageLinks ?? [];
  return (
    <div className="space-y-1">
      <p className="text-body">
        {d.idImages === null ? <span className="text-tg-hint">How many is not known</span>
          : <span className={d.idImages < 2 ? 'text-warn' : ''}>{d.idImages} of 2 — in the ops group</span>}
      </p>
      {ids.length > 0 && (
        <p className="flex flex-wrap gap-x-4" data-id-links="">
          {ids.map((url, i) => (
            <a key={`${i}:${url}`} className="btn-link text-body max-lg:py-2.5" href={url} target="_blank" rel="noopener noreferrer" aria-label={`Open ID image ${i + 1} in the ops group`}
              onClick={(e) => { e.preventDefault(); openChat(url); }}>
              ID image {i + 1}
            </a>
          ))}
        </p>
      )}
    </div>
  );
}

// ── the conversation ─────────────────────────────────────────────────────────────────────────────────────────────

const APP_WORD: Record<string, string> = { telegram: 'Telegram', web: 'Website', whatsapp: 'WhatsApp' };
const homeOf = (d: LeadRecord): 'telegram' | 'web' | 'whatsapp' => (d.channel === 'web' ? 'web' : 'telegram');
/** A day's name, counted to the read: "Today", "Yesterday", else "05 Oct". */
function dayName(iso: string, readMs: number): string {
  const day = fmtDay(iso, true);
  if (Number.isFinite(readMs)) {
    if (day === fmtDay(readMs, true)) return 'Today';
    if (day === fmtDay(readMs - 86_400_000, true)) return 'Yesterday';
  }
  return fmtDay(iso);
}

/** Every chat of the person as one conversation, opening on the newest message: the customer on the left; the bot and
 *  the team on the right — the team's own words as "You"; a message from another chat says which. */
export function Chat({ d, readMs }: { d: LeadRecord; readMs: number }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [d.chat.length]);
  if (d.chat.length === 0) return <EmptyState>No messages on record.</EmptyState>;
  const home = homeOf(d);
  let last = '';
  return (
    <div ref={box} className="max-h-[60vh] overflow-y-auto overscroll-contain rounded-lg border border-stroke px-3 py-3" role="log" aria-label="Every chat, oldest first">
      <ol className="space-y-2">
        {d.chat.map((m, i) => {
          const day = m.at ? dayName(m.at, readMs) : '';
          const sep = day !== '' && day !== last;
          if (day) last = day;
          const mine = m.from !== 'customer';
          const bubble = m.from === 'customer' ? 'bg-panel-2' : m.from === 'team' ? 'bg-brand-teal/15' : 'bg-panel-3 text-tg-hint';
          const elsewhere = m.channel && m.channel !== home ? APP_WORD[m.channel] : null;
          return (
            <li key={i} data-from={m.from}>
              {sep && <p className="my-2 text-center text-meta text-tg-hint">{day}</p>}
              <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 ${bubble}`}>
                  {(m.from !== 'customer' || elsewhere) && (
                    <p className="text-meta">
                      {m.from === 'team' && <span className="font-medium text-brand-teal">You</span>}
                      {m.from === 'bot' && <span className="text-tg-hint">Bot</span>}
                      {elsewhere && <span className="text-tg-hint"> · {elsewhere}</span>}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap break-words text-body leading-snug">{m.text}</p>
                  {m.at && <p className="text-right text-meta tabular-nums text-tg-hint">{fmtTime(m.at)}</p>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ── the history ──────────────────────────────────────────────────────────────────────────────────────────────────

/** The latest decision on the record, from its timeline; null when none was made. */
function decisionOf(d: LeadRecord): LeadRecord['timeline'][number] | null {
  for (let i = d.timeline.length - 1; i >= 0; i--) {
    const t = d.timeline[i]!;
    if (t.kind === 'approved' || t.kind === 'rejected') return t;
  }
  return null;
}
const KIND_TONE: Record<string, string> = { approved: 'text-pos', rejected: 'text-neg', refused: 'text-neg', declined: 'text-warn', card_failed: 'text-warn' };

/** The decision first — made in the ops group, never here — then what was done, newest first. */
export function History({ d }: { d: LeadRecord }) {
  const decision = decisionOf(d);
  const items = [...d.timeline].reverse();
  return (
    <div className="space-y-3">
      <p className="text-body">
        <span className="text-tg-hint">Decision: </span>
        {decision
          ? <><span className={`font-medium ${decision.kind === 'rejected' ? 'text-neg' : ''}`}>{decision.kind === 'rejected' ? 'Rejected' : 'Approved'}</span>{decision.by && <> by {decision.by}</>}{decision.at && <> · <span className="tabular-nums">{fmtDate(decision.at)}</span></>}</>
          : d.state === 'awaiting_approval' ? <span className="font-medium text-warn">Waiting for your decision</span> : <span className="text-tg-hint">No decision yet</span>}
      </p>
      {items.length === 0 ? <EmptyState>Nothing recorded yet.</EmptyState> : (
        <ol className="space-y-3 border-l border-stroke pl-4" aria-label="What was done, newest first">
          {items.map((t, i) => (
            <li key={i} data-kind={t.kind ?? 'note'}>
              <p className={`text-body leading-snug ${KIND_TONE[t.kind ?? ''] ?? ''}`}><Clip text={t.what} /></p>
              <p className="text-meta tabular-nums text-tg-hint">{t.at ? fmtDate(t.at) : 'time not known'}{t.by && <> · {t.by}</>}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ── their party in the Books ─────────────────────────────────────────────────────────────────────────────────────

/** Their party in the Books (D3, 15 Oct 2026): linked — its name, a link to its page; approved and not linked — Make a
 *  party (the screen's one gold primary), and when a party of that name exists, the choice to link this customer to it
 *  or make a second party under a numbered name; not approved — nothing; a worker that sends no customer id — said. */
export function PartyAction({ d }: { d: LeadRecord }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; tone: 'plain' | 'warn' } | null>(null);
  const [taken, setTaken] = useState<PartyLink | null>(null);
  const run = async (how: { link?: string; slug?: string } = {}) => {
    if (busy) return;
    setBusy(true);
    setSaid(null);
    try {
      const r = await onboardingApi.makeParty(d.id, how);
      setTaken(null);
      setSaid(madePartyWords(r));
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    } catch (e) {
      const clash = partyNameTaken(e);
      if (clash) setTaken(clash.existing);
      else setSaid({ text: `Couldn't make the party — ${failWords(e)}`, tone: 'warn' }); // words:ok — the server's sentence
    } finally {
      setBusy(false);
    }
  };
  const note = said && <p className={`text-meta ${said.tone === 'warn' ? 'text-warn' : 'text-tg-hint'}`} role="status">{said.text}</p>;
  if (d.party) {
    return (
      <div className="space-y-1" data-party={d.party.slug}>
        <Link to={`/parties/${encodeURIComponent(d.party.slug)}?entity=${d.party.entity}`} className="btn-link text-body font-medium" data-party-link="">Party: {d.party.name} →</Link>
        {note}
      </div>
    );
  }
  if (d.state !== 'done') return null;
  if (!d.customerId) return <p className="text-meta text-tg-hint" data-party="">Make a party waits for the bot&apos;s update.</p>;
  return (
    <div className="space-y-2" data-party="">
      {!taken && (
        <button type="button" className="btn-primary w-full px-4 text-body disabled:opacity-60 lg:w-auto" disabled={busy} onClick={() => run()} data-make-party=""
          aria-label="Make a party in the Books from this record">
          {busy ? 'Making…' : 'Make a party'}
        </button>
      )}
      {taken && (
        <div className="space-y-1 rounded-lg bg-warn-bg p-3 text-body text-warn" role="status">
          <p>A party named <strong>{taken.name}</strong> already exists.</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <button type="button" className="btn-link max-lg:py-2.5" disabled={busy} onClick={() => run({ link: taken.slug })}>Link to it</button>
            <button type="button" className="btn-link max-lg:py-2.5" disabled={busy} onClick={() => run({ slug: `${taken.slug}-2` })}>Make {taken.slug}-2</button>
            <button type="button" className="btn-link max-lg:py-2.5" disabled={busy} onClick={() => setTaken(null)}>Cancel</button>
          </div>
        </div>
      )}
      {note}
    </div>
  );
}

/** The sanctions verdict's source and its lists' refresh (main ada2efbb); read loosely so a Worker before it answers null. */
type SanctionsSourced = { sanctionsSource?: string | null; sanctionsListAt?: string | null };
const sanctionsSource = (w: object): string | null => (w as SanctionsSourced).sanctionsSource || null;
const sanctionsListAt = (w: object): string | null => (w as SanctionsSourced).sanctionsListAt || null;
